import { useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Button } from '@/components/ui/button';
import { useComments, useDeleteComment, CommentWithProfile } from '@/hooks/useComments';
import { useToggleCommentLike } from '@/hooks/useLikes';
import { useAuth } from '@/contexts/AuthContext';
import { useIsModerator } from '@/hooks/useUserRole';
import { CommentInput } from './CommentInput';
import { LinkifiedText } from './LinkifiedText';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Trash2, MessageCircle, Heart, Reply, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMembershipContext } from '@/components/layout/AppLayout';

interface CommentSectionProps {
  postId: string;
  initialCount?: number;
}

export function CommentSection({ postId, initialCount = 0 }: CommentSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: comments, isLoading } = useComments(postId);
  const deleteComment = useDeleteComment();
  const { user } = useAuth();
  const { isModerator } = useIsModerator();
  const { isPending } = useMembershipContext();

  // Count all comments including replies
  const totalCount = comments?.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0) ?? initialCount;

  const handleDelete = async (commentId: string) => {
    if (!confirm('Delete this comment?')) return;
    await deleteComment.mutateAsync({ commentId, postId });
  };

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="inline-flex items-center gap-1.5 bg-muted/50 border border-border rounded-full px-3 py-1.5 text-muted-foreground text-xs hover:bg-muted hover:border-border/80 transition-colors"
      >
        <MessageCircle className="h-3.5 w-3.5" />
        <span>{totalCount}</span>
      </button>
    );
  }

  return (
    <div className="w-full space-y-3 pt-4 border-t border-border">
      {/* Toggle button */}
      <button
        onClick={() => setIsExpanded(false)}
        className="inline-flex items-center gap-1.5 bg-muted/50 border border-border rounded-full px-3 py-1.5 text-muted-foreground text-xs hover:bg-muted hover:border-border/80 transition-colors mb-1"
      >
        <MessageCircle className="h-3.5 w-3.5 fill-current" />
        <span>Hide comments ({totalCount})</span>
      </button>

      {/* Comment Input */}
      <div className="w-full">
        <CommentInput postId={postId} />
      </div>

      {/* Comments List */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : comments && comments.length > 0 ? (
        <div className="space-y-3">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              postId={postId}
              canDelete={user?.id === comment.user_id || isModerator}
              onDelete={() => handleDelete(comment.id)}
              currentUserId={user?.id}
              disableActions={isPending}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground text-center py-3">
          No comments yet. Be the first to comment!
        </p>
      )}
    </div>
  );
}

interface CommentItemProps {
  comment: CommentWithProfile;
  postId: string;
  canDelete: boolean;
  onDelete: () => void;
  currentUserId?: string;
  isReply?: boolean;
  disableActions?: boolean;
}

function CommentItem({ comment, postId, canDelete, onDelete, currentUserId, isReply = false, disableActions = false }: CommentItemProps) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const { toggleLike, isPending: isLiking } = useToggleCommentLike();
  const deleteComment = useDeleteComment();
  const { isModerator } = useIsModerator();

  const handleLike = async () => {
    if (!currentUserId) return;
    if (disableActions) return;
    await toggleLike(comment.id, postId, comment.isLikedByUser || false);
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!confirm('Delete this reply?')) return;
    await deleteComment.mutateAsync({ commentId: replyId, postId });
  };

  const repliesCount = comment.replies?.length || 0;

  return (
    <div className={cn('flex gap-2 sm:gap-3 group items-start w-full', isReply && 'ml-8 sm:ml-10')}>
      <Avatar className={cn('shrink-0', isReply ? 'h-6 w-6' : 'h-7 w-7 sm:h-8 sm:w-8')}>
        <SignedAvatarImage src={comment.profiles?.avatar_url || undefined} />
        <AvatarFallback
          className={cn('text-white', isReply ? 'text-[9px]' : 'text-[10px] sm:text-xs')}
          style={{
            background: 'linear-gradient(135deg, hsl(340 82% 52%) 0%, hsl(255 100% 65%) 100%)',
          }}
        >
          {comment.profiles?.full_name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 max-w-full">
        <div className="bg-muted/40 rounded-2xl px-3 py-2 sm:px-4 sm:py-2.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className={cn('text-foreground font-medium', isReply ? 'text-[11px]' : 'text-xs sm:text-[13px]')}>
              {comment.profiles?.full_name || 'Unknown'}
            </span>
            <span className={cn('text-muted-foreground', isReply ? 'text-[9px]' : 'text-[10px] sm:text-xs')}>
              {formatDistanceToNow(new Date(comment.created_at), {
                addSuffix: true,
              })}
            </span>
          </div>
          <p className={cn('text-foreground/80 mt-0.5 whitespace-pre-wrap break-words leading-relaxed', isReply ? 'text-[11px]' : 'text-xs sm:text-sm')}>
            <LinkifiedText text={comment.content} />
          </p>
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-3 mt-1 ml-1">
          {/* Like button */}
          <button
            onClick={handleLike}
            disabled={isLiking || !currentUserId || disableActions}
            className={cn(
              'flex items-center gap-1 text-[10px] sm:text-xs transition-colors',
              comment.isLikedByUser
                ? 'text-pink-500'
                : 'text-muted-foreground hover:text-pink-500',
              (!currentUserId || isLiking || disableActions) && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Heart className={cn('h-3 w-3', comment.isLikedByUser && 'fill-current')} />
            {comment.likes_count > 0 && <span>{comment.likes_count}</span>}
          </button>

          {/* Reply button - only for top-level comments */}
          {!isReply && (
            <button
              onClick={() => !disableActions && setShowReplyInput(!showReplyInput)}
              className={cn(
                'flex items-center gap-1 text-[10px] sm:text-xs transition-colors',
                disableActions ? 'text-muted-foreground/50 cursor-not-allowed' : 'text-muted-foreground hover:text-primary'
              )}
            >
              <Reply className="h-3 w-3" />
              <span>Reply</span>
            </button>
          )}

          {/* Delete button */}
          {canDelete && (
            <button
              onClick={onDelete}
              className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 max-sm:opacity-100"
            >
              <Trash2 className="h-3 w-3" />
              <span>Delete</span>
            </button>
          )}
        </div>

        {/* Reply input */}
        {showReplyInput && (
          <div className="mt-2">
            <CommentInput
              postId={postId}
              parentCommentId={comment.id}
              placeholder={`Reply to ${comment.profiles?.full_name || 'comment'}...`}
              compact
              autoFocus
              onCommentAdded={() => setShowReplyInput(false)}
            />
          </div>
        )}

        {/* Replies */}
        {repliesCount > 0 && (
          <div className="mt-2">
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="flex items-center gap-1 text-[10px] sm:text-xs text-primary hover:underline mb-2"
            >
              {showReplies ? (
                <>
                  <ChevronUp className="h-3 w-3" />
                  <span>Hide {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}</span>
                </>
              ) : (
                <>
                  <ChevronDown className="h-3 w-3" />
                  <span>View {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}</span>
                </>
              )}
            </button>

            {showReplies && (
              <div className="space-y-2">
                {comment.replies?.map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    postId={postId}
                    canDelete={currentUserId === reply.user_id || isModerator}
                    onDelete={() => handleDeleteReply(reply.id)}
                    currentUserId={currentUserId}
                    isReply
                    disableActions={disableActions}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
